// Amazon Cognito Hosted UI, Authorization Code + PKCE (NFR2.1 / SD1 / SD3 / D4).
// The access token lives IN MEMORY only (never localStorage/sessionStorage) to limit
// XSS token theft. The PKCE code_verifier and a transient dirty-form snapshot ride the
// full-page redirect via sessionStorage (tab-scoped, non-persistent) and are cleared
// immediately after use. Non-secret public config comes from build-time VITE_* env.

interface AuthConfig {
  domain: string; // e.g. https://<prefix>.auth.<region>.amazoncognito.com
  clientId: string;
  redirectUri: string;
}

function config(): AuthConfig {
  const env = import.meta.env;
  return {
    domain: env.VITE_COGNITO_DOMAIN ?? "",
    clientId: env.VITE_COGNITO_CLIENT_ID ?? "",
    redirectUri: env.VITE_COGNITO_REDIRECT_URI ?? `${location.origin}/`,
  };
}

let accessToken: string | null = null;

export function getToken(): string | null {
  return accessToken;
}

export function clearToken(): void {
  accessToken = null;
}

export function isAuthenticated(): boolean {
  return accessToken !== null;
}

// -- PKCE helpers -----------------------------------------------------------
function base64Url(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function sha256(input: string): Promise<ArrayBuffer> {
  return crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
}

function randomVerifier(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return base64Url(bytes.buffer);
}

const VERIFIER_KEY = "pkce_verifier";

/** Redirect to the Cognito Hosted UI to begin sign-in. */
export async function login(): Promise<void> {
  const { domain, clientId, redirectUri } = config();
  const verifier = randomVerifier();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  const challenge = base64Url(await sha256(verifier));
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: "openid",
    code_challenge_method: "S256",
    code_challenge: challenge,
  });
  location.assign(`${domain}/oauth2/authorize?${params.toString()}`);
}

export function logout(): void {
  clearToken();
  const { domain, clientId, redirectUri } = config();
  location.assign(
    `${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(redirectUri)}`,
  );
}

/**
 * If the URL carries an auth `code`, exchange it for tokens (PKCE), store the access
 * token in memory, and strip the code from the URL. Returns true when a sign-in completed.
 */
export async function handleRedirectCallback(): Promise<boolean> {
  const url = new URL(location.href);
  const code = url.searchParams.get("code");
  if (!code) return false;
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  if (!verifier) return false;

  const { domain, clientId, redirectUri } = config();
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: verifier,
  });
  const res = await fetch(`${domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) return false;
  const tokens = (await res.json()) as { access_token?: string; id_token?: string };
  // The API Gateway Cognito authorizer (no method scopes configured) validates the
  // IDENTITY token, not the access token — send the id_token as the bearer.
  accessToken = tokens.id_token ?? tokens.access_token ?? null;
  // Strip the code from the address bar (no token in the URL).
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  history.replaceState({}, "", url.pathname + url.search);
  return accessToken !== null;
}

// -- Dirty-form snapshot across the redirect (SD3 / WCAG 2.2.1) --------------
export function saveFormSnapshot(key: string, value: unknown): void {
  sessionStorage.setItem(`form_snapshot:${key}`, JSON.stringify(value));
}

export function takeFormSnapshot<T>(key: string): T | null {
  const raw = sessionStorage.getItem(`form_snapshot:${key}`);
  if (raw === null) return null;
  sessionStorage.removeItem(`form_snapshot:${key}`);
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
